import {
    Box,
    Typography,
    TextField,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popper,
    ClickAwayListener,
    Grid,
    Tooltip,
    IconButton,
    Collapse,
    Chip,
    Divider,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Snackbar
} from '@mui/material';
import GlobalPagination from '../../../components/GlobalPagination';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../components/cards/MainCard';
import GlobalDateRangeSelector from '../../../components/GlobalDateRangeSelector';
import { getDateRangeDisplayText, calculateDateRange } from '../../../utils/dateRangeUtils';
import SearchIcon from '@mui/icons-material/Search';
import HelpIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email'; // ADD: Import email icon
import React, { useState, useMemo } from 'react';
import { useDeleteUserSentCoursesMutation, useSendCourseReminderMutation } from '../../../store/services/response.service'; // ADD: Import reminder mutation
import GlobalAnimatedProgressBar from '../../../components/GlobalAnimatedProgressBar';

const UserSearch = ({
    searchQuery,
    setSearchQuery,
    anchorEl,
    filteredUsers,
    isSearching,
    handleClickAway,
    handleSelectUser,
    selectedUserId,
    userData,
    userDataLoading,
    theme: providedTheme,
    dateRange,
    setDateRange,
    refetchUserData
}) => {
    const defaultTheme = useTheme();
    const theme = providedTheme || defaultTheme;
    const [outstandingCoursesExpanded, setOutstandingCoursesExpanded] = useState(false);
    const [outstandingCoursesPage, setOutstandingCoursesPage] = useState(1);
    const [completedCoursesExpanded, setCompletedCoursesExpanded] = useState(false);
    const [completedCoursesPage, setCompletedCoursesPage] = useState(1);

    // Custom date range states
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Delete modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const [sendingReminder, setSendingReminder] = useState(null);

    const [deleteUserSentCourses, { isLoading: deleteLoading }] = useDeleteUserSentCoursesMutation();

    const [sendCourseReminder, { isLoading: reminderLoading }] = useSendCourseReminderMutation();

    const COURSES_PER_PAGE = 4;

    // Check if a course is recent (within specified days)
    const isRecent = (dateString, days) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= days;
    };

    // Function to calculate course score percentage
    const getCourseScorePercentage = (course) => {
        // Check for different possible score properties in your data
        if (course.Score !== undefined && course.Score !== null) {
            return Math.round(course.Score);
        }
        if (course.FinalScore !== undefined && course.FinalScore !== null) {
            return Math.round(course.FinalScore);
        }
        if (course.Percentage !== undefined && course.Percentage !== null) {
            return Math.round(course.Percentage);
        }
        if (course.CorrectAnswers !== undefined && course.TotalQuestions > 0) {
            return Math.round((course.CorrectAnswers / course.TotalQuestions) * 100);
        }
        return null;
    };

    // Function to get score color based on percentage
    const getScoreColor = (percentage) => {
        if (percentage >= 80) return 'success.main';
        return 'warning.main';
    };

    // Function to format score display
    const formatScoreDisplay = (course) => {
        const scorePercentage = getCourseScorePercentage(course);
        if (scorePercentage !== null) {
            return `${scorePercentage}%`;
        }
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    const handleCustomStartDateChange = (event) => {
        setCustomStartDate(event.target.value);
    };

    const handleCustomEndDateChange = (event) => {
        setCustomEndDate(event.target.value);
    };

    // Calculate actual dates based on selection using the utility
    const dateParams = useMemo(() => {
        return calculateDateRange(dateRange, customStartDate, customEndDate);
    }, [dateRange, customStartDate, customEndDate]);

    // Filter courses by date range
    const isWithinDateRange = (course) => {
        if (!course.DateSent) return false;
        const sentDate = new Date(course.DateSent);
        return sentDate >= dateParams.startDate && sentDate <= dateParams.endDate;
    };

    // Delete handlers
    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!courseToDelete || !userData) return;

        // Safety checks for required parameters
        if (!userData.Email || !courseToDelete.UniqueIdentifier) {
            setSnackbarMessage('Missing required information for deletion');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setDeleteModalOpen(false);
            return;
        }

        try {
            await deleteUserSentCourses({
                userId: userData.Email,
                uniqueIdentifier: courseToDelete.UniqueIdentifier
            }).unwrap();

            setSnackbarMessage('Course deleted successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Close modal and reset state
            setDeleteModalOpen(false);
            setCourseToDelete(null);

            // Manually refetch user data to update the UI immediately
            if (refetchUserData) {
                await refetchUserData();
            }

        } catch (error) {
            console.error('Delete error:', error);
            setSnackbarMessage(error?.data?.message || 'Failed to delete course. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);

            // Close modal even on error
            setDeleteModalOpen(false);
            setCourseToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setCourseToDelete(null);
    };

    // ADD: Send reminder handlers
    const handleSendReminderClick = async (course) => {
        if (!userData || !course) return;

        // Safety checks for required parameters
        if (!userData.Email || !course.CourseId || !course.UniqueIdentifier) {
            setSnackbarMessage('Missing required information to send reminder');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setSendingReminder(course.UniqueIdentifier);

        try {
            await sendCourseReminder({
                userId: userData.Email,
                courseId: course.CourseId,
                uniqueIdentifier: course.UniqueIdentifier
            }).unwrap();

            setSnackbarMessage(`Reminder sent successfully for "${course.CourseId}"`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

        } catch (error) {
            console.error('Send reminder error:', error);
            setSnackbarMessage(error?.data?.message || 'Failed to send reminder. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setSendingReminder(null);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Calculate completion percentages based on filtered data
    const getFilteredCourseStats = () => {
        if (!userData?.Courses) return { sent: 0, completed: 0 };

        const filteredCourses = userData.Courses.filter(course => isWithinDateRange(course));
        const completed = filteredCourses.filter(course => course.DateCompleted !== null);

        return {
            sent: filteredCourses.length,
            completed: completed.length
        };
    };

    const getCourseCompletionPercentage = () => {
        const { sent, completed } = getFilteredCourseStats();
        if (sent === 0) return 0;
        return (completed / sent) * 100;
    };

    const getSimulationClickPercentage = () => {
        if (!userData?.SimulationSummaryData?.SimulationsSent || userData.SimulationSummaryData.SimulationsSent === 0) return 0;
        return (userData.SimulationSummaryData.SimulationsClicked / userData.SimulationSummaryData.SimulationsSent) * 100;
    };

    // Get outstanding courses (courses with DateCompleted: null) within date range
    const getOutstandingCourses = () => {
        if (!userData?.Courses) return [];
        return userData.Courses.filter(course =>
            course.DateCompleted === null && isWithinDateRange(course)
        );
    };

    // Get completed courses (courses with DateCompleted: not null) within date range
    const getCompletedCourses = () => {
        if (!userData?.Courses) return [];
        return userData.Courses.filter(course =>
            course.DateCompleted !== null && isWithinDateRange(course)
        );
    };

    const outstandingCourses = getOutstandingCourses();
    const completedCourses = getCompletedCourses();
    const totalOutstandingPages = Math.ceil(outstandingCourses.length / COURSES_PER_PAGE);
    const totalCompletedPages = Math.ceil(completedCourses.length / COURSES_PER_PAGE);

    // Get courses for current page - outstanding
    const getCurrentPageOutstandingCourses = () => {
        const startIndex = (outstandingCoursesPage - 1) * COURSES_PER_PAGE;
        const endIndex = startIndex + COURSES_PER_PAGE;
        return outstandingCourses.slice(startIndex, endIndex);
    };

    // Get courses for current page - completed
    const getCurrentPageCompletedCourses = () => {
        const startIndex = (completedCoursesPage - 1) * COURSES_PER_PAGE;
        const endIndex = startIndex + COURSES_PER_PAGE;
        return completedCourses.slice(startIndex, endIndex);
    };

    const currentPageOutstandingCourses = getCurrentPageOutstandingCourses();
    const currentPageCompletedCourses = getCurrentPageCompletedCourses();

    // Reset pagination when user changes or date range changes
    React.useEffect(() => {
        setOutstandingCoursesPage(1);
        setCompletedCoursesPage(1);
    }, [selectedUserId, dateRange, customStartDate, customEndDate]);

    const handleOutstandingCoursesPageChange = (event, value) => {
        setOutstandingCoursesPage(value);
    };

    const handleCompletedCoursesPageChange = (event, value) => {
        setCompletedCoursesPage(value);
    };

    return (
        <MainCard
            content={false}
            sx={{
                width: "100%",
                bgcolor: "background.default"
            }}
            border={false}
            boxShadow={true}
        >
            <Box sx={{ p: 2 }}>
                {/* SearchUser Section */}
                <Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                            <SearchIcon sx={{ mr: 0.5, fontSize: '1rem' }} color="primary" />
                            User Search
                        </Typography>

                        <Tooltip title="Search for users within your selected tenancy to view their course completion and simulation performance data. Simulation clicks may take up to a day to register per user.">
                            <IconButton size="small">
                                <HelpIcon sx={{ color: "secondary.main" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Search and Date Range Row */}
                    <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                        <Grid item xs={6}>
                            <ClickAwayListener onClickAway={handleClickAway}>
                                <Box sx={{ position: 'relative' }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e)}
                                        placeholder="Search by name or email..."
                                        InputProps={{
                                            endAdornment: isSearching ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null,
                                        }}
                                        disabled={userDataLoading && !userData}
                                    />
                                    <Popper
                                        open={filteredUsers.length > 0}
                                        anchorEl={anchorEl}
                                        placement="bottom-start"
                                        style={{ width: anchorEl?.offsetWidth, zIndex: 1200 }}
                                    >
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                mt: 0.5,
                                                maxHeight: 250,
                                                overflow: 'auto',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <List sx={{ p: 0 }}>
                                                {filteredUsers.map((user) => (
                                                    <ListItem
                                                        key={user.id}
                                                        buttonbase
                                                        onClick={() => handleSelectUser(user)}
                                                        sx={{
                                                            '&:hover': {
                                                                bgcolor: 'action.hover'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={user.name}
                                                            secondary={user.email}
                                                            primaryTypographyProps={{ fontWeight: 'medium' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    </Popper>
                                </Box>
                            </ClickAwayListener>
                        </Grid>

                        <Grid item xs={6}>
                            <GlobalDateRangeSelector
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                onCustomStartDateChange={handleCustomStartDateChange}
                                onCustomEndDateChange={handleCustomEndDateChange}
                                customStartDate={customStartDate}
                                customEndDate={customEndDate}
                                includeCustom={true}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    {/* Current date range indicator */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Current: {getDateRangeDisplayText(dateRange, customStartDate, customEndDate)}
                    </Typography>

                    {!selectedUserId ? (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Please select a user to view their data
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {/* Placeholder boxes to show what data will be displayed */}
                                <Grid item xs={6}>
                                    <MainCard sx={{
                                        bgcolor: 'primary.main',
                                        borderRadius: 1,
                                        color: 'primary.contrastText',
                                        height: '100%',
                                        opacity: 0.6,
                                        position: 'relative'
                                    }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Courses</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">Sent:</Typography>
                                            <Typography variant="body2" fontWeight="bold">--</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption">Completed:</Typography>
                                            <Typography variant="body2" fontWeight="bold">--</Typography>
                                        </Box>
                                    </MainCard>
                                </Grid>

                                <Grid item xs={6}>
                                    <MainCard sx={{
                                        borderColor: 'secondary.dark',
                                        borderWidth: '2px !important',
                                        borderStyle: 'solid',
                                        borderRadius: 1,
                                        color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.dark',
                                        height: '100%',
                                        opacity: 0.6,
                                        position: 'relative'
                                    }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Simulations</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">Sent:</Typography>
                                            <Typography variant="body2" fontWeight="bold">--</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption">Clicked:</Typography>
                                            <Typography variant="body2" fontWeight="bold">--</Typography>
                                        </Box>
                                    </MainCard>
                                </Grid>

                                {/* Placeholder completion rates side by side */}
                                <Grid item xs={6}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        p: 2,
                                        bgcolor: "background.default",
                                        opacity: 0.6,
                                        position: 'relative',
                                        height: '100%'
                                    }} border={false} boxShadow={true}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Course Completion Rate</Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1
                                        }}>
                                            <Typography variant="caption">-- of -- courses completed</Typography>
                                            <Typography variant="body2" fontWeight="bold">--%</Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={0}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                mt: 2,
                                                mb: 2,
                                                backgroundColor: 'grey.200',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: 'success.main',
                                                    borderRadius: 4
                                                }
                                            }}
                                        />
                                    </MainCard>
                                </Grid>

                                <Grid item xs={6}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        p: 2,
                                        bgcolor: "background.default",
                                        opacity: 0.6,
                                        position: 'relative',
                                        height: '100%'
                                    }} border={false} boxShadow={true}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Simulation Click Rate</Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1
                                        }}>
                                            <Typography variant="caption">-- of -- simulations clicked</Typography>
                                            <Typography variant="body2" fontWeight="bold">--%</Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={0}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                mt: 2,
                                                mb: 2,
                                                backgroundColor: 'grey.200',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: 'primary.main',
                                                    borderRadius: 4
                                                }
                                            }}
                                        />
                                    </MainCard>
                                </Grid>

                                {/* Outstanding Courses Placeholder */}
                                <Grid item xs={12}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        bgcolor: "background.default",
                                        opacity: 0.6
                                    }} border={false} boxShadow={true}>
                                        <Box sx={{
                                            p: 2,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Outstanding Courses
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" disabled>
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Box>
                                    </MainCard>
                                </Grid>

                                {/* Completed Courses Placeholder */}
                                <Grid item xs={12}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        bgcolor: "background.default",
                                        opacity: 0.6
                                    }} border={false} boxShadow={true}>
                                        <Box sx={{
                                            p: 2,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Completed Courses
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" disabled>
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Box>
                                    </MainCard>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : userDataLoading ? (
                        <Box sx={{
                            height: 455,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                                Loading user data...
                            </Typography>
                        </Box>
                    ) : userData ? (
                        <Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2
                            }}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    {userData.FirstName} {userData.LastName}
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {/* Course Statistics */}
                                <Grid item xs={6}>
                                    <MainCard sx={{
                                        bgcolor: 'primary.main',
                                        borderRadius: 1,
                                        color: 'primary.contrastText',
                                        height: '100%'
                                    }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Courses</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">Sent:</Typography>
                                            <Typography variant="body2" fontWeight="bold">{getFilteredCourseStats().sent}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption">Completed:</Typography>
                                            <Typography variant="body2" fontWeight="bold">{getFilteredCourseStats().completed}</Typography>
                                        </Box>
                                    </MainCard>
                                </Grid>

                                {/* Simulation Statistics */}
                                <Grid item xs={6}>
                                    <MainCard
                                        sx={{
                                            borderColor: 'secondary.dark',
                                            borderWidth: '2px !important',
                                            borderStyle: 'solid',
                                            borderRadius: 1,
                                            color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.dark',
                                            height: '100%'
                                        }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Simulations</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">Sent:</Typography>
                                            <Typography variant="body2" fontWeight="bold">{userData?.SimulationSummaryData?.SimulationsSent || 0}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption">Clicked:</Typography>
                                            <Typography variant="body2" fontWeight="bold">{userData?.SimulationSummaryData?.SimulationsClicked || 0}</Typography>
                                        </Box>
                                    </MainCard>
                                </Grid>

                                {/* Course Completion Rate with CountUp */}
                                <Grid item xs={6}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        p: 2,
                                        bgcolor: "background.default",
                                        height: '100%'
                                    }} border={false} boxShadow={true}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Course Completion Rate
                                        </Typography>

                                        <GlobalAnimatedProgressBar
                                            percentage={getCourseCompletionPercentage()}
                                            value={getFilteredCourseStats().completed}
                                            total={getFilteredCourseStats().sent}
                                            colorScheme="secondary"
                                            showCounts={true}
                                            showPercentage={true}
                                            showLabel={false}
                                            textPosition="right"
                                            animationDelay={0}
                                            resetKey={`${selectedUserId}-${dateRange}-${customStartDate}-${customEndDate}`}
                                            triggerAnimation={!!userData && !userDataLoading}
                                            size="small"
                                        />
                                    </MainCard>
                                </Grid>

                                {/* Simulation Click Rate with CountUp */}
                                <Grid item xs={6}>
                                    <MainCard content={false} sx={{
                                        width: "100%",
                                        p: 2,
                                        bgcolor: "background.default",
                                        height: '100%'
                                    }} border={false} boxShadow={true}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Simulation Click Rate
                                        </Typography>

                                        <GlobalAnimatedProgressBar
                                            percentage={getSimulationClickPercentage()}
                                            value={userData?.SimulationSummaryData?.SimulationsClicked || 0}
                                            total={userData?.SimulationSummaryData?.SimulationsSent || 0}
                                            colorScheme="gradient"
                                            showCounts={true}
                                            showPercentage={true}
                                            showLabel={false}
                                            textPosition="right"
                                            animationDelay={0.3}
                                            resetKey={`${selectedUserId}-${dateRange}-${customStartDate}-${customEndDate}`}
                                            triggerAnimation={!!userData && !userDataLoading}
                                            size="small"
                                        />
                                    </MainCard>
                                </Grid>

                                {/* Outstanding Courses Expandable Section */}
                                <Grid item xs={12}>
                                    <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                                        <Box
                                            onClick={() => setOutstandingCoursesExpanded(!outstandingCoursesExpanded)}
                                            sx={{
                                                p: 2,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Outstanding Courses
                                                </Typography>
                                                <Chip
                                                    label={outstandingCourses.length}
                                                    size="small"
                                                    sx={{
                                                        ml: 1,
                                                        backgroundColor: '#33BD95',
                                                        color: 'white'
                                                    }}
                                                />
                                            </Box>
                                            <IconButton size="small">
                                                {outstandingCoursesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>

                                        <Collapse in={outstandingCoursesExpanded}>
                                            <Divider />
                                            <Box sx={{ p: 2 }}>
                                                {outstandingCourses.length === 0 ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                                                        No outstanding courses found for this user within the selected date range.
                                                    </Typography>
                                                ) : (
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                                            Courses sent but not yet completed within the selected date range {outstandingCourses.length > COURSES_PER_PAGE && `(${outstandingCourses.length} total)`}:
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ minHeight: '160px' }}>
                                                            {currentPageOutstandingCourses.map((course) => (
                                                                <Grid item xs={6} key={course.UniqueIdentifier}>
                                                                    <MainCard
                                                                        content={false}
                                                                        sx={{
                                                                            p: 1.5,  // Reduced padding
                                                                            border: '1px solid',
                                                                            // Remove fixed height to let content determine size
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            // Remove justifyContent: 'space-between'
                                                                            position: 'relative'
                                                                        }}
                                                                        border={true}
                                                                        boxShadow={false}
                                                                    >
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            top: 8,
                                                                            right: 4,
                                                                            display: 'flex'
                                                                        }}>
                                                                            {/* Send Reminder Button */}
                                                                            <Tooltip title="Send Reminder">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleSendReminderClick(course);
                                                                                    }}
                                                                                    disabled={sendingReminder === course.UniqueIdentifier}
                                                                                    sx={{
                                                                                        backgroundColor: 'rgba(33, 189, 149, 0.1)',
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(33, 189, 149, 0.2)',
                                                                                            color: '#33BD95'
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {sendingReminder === course.UniqueIdentifier ? (
                                                                                        <CircularProgress size={16} />
                                                                                    ) : (
                                                                                        <EmailIcon fontSize="small" sx={{ color: '#33BD95' }} />
                                                                                    )}
                                                                                </IconButton>
                                                                            </Tooltip>

                                                                            {/* Delete Button */}
                                                                            <Tooltip title="Delete Course">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDeleteClick(course);
                                                                                    }}
                                                                                    sx={{
                                                                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                                                            color: 'error.main'
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Box>

                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, pr: 5.5 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 500,
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 2,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    lineHeight: 1.3,
                                                                                    flex: 1,
                                                                                    pr: 2
                                                                                }}
                                                                            >
                                                                                {course.CourseId}
                                                                            </Typography>
                                                                            {isRecent(course.DateSent, 30) && (
                                                                                <Chip
                                                                                    label="NEW"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        height: 20,
                                                                                        fontSize: '0.7rem',
                                                                                        fontWeight: 'bold',
                                                                                        px: 0.5,
                                                                                        backgroundColor: '#33BD95',
                                                                                        color: 'white',
                                                                                        mr: 2,
                                                                                        '& .MuiChip-label': {
                                                                                            px: 0.5
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            Sent: {new Date(course.DateSent).toLocaleDateString()}
                                                                        </Typography>
                                                                    </MainCard>
                                                                </Grid>
                                                            ))}
                                                        </Grid>

                                                        {totalOutstandingPages > 1 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                <GlobalPagination
                                                                    count={totalOutstandingPages}
                                                                    page={outstandingCoursesPage}
                                                                    onChange={handleOutstandingCoursesPageChange}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </MainCard>
                                </Grid>

                                {/* Completed Courses Expandable Section */}
                                <Grid item xs={12}>
                                    <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                                        <Box
                                            onClick={() => setCompletedCoursesExpanded(!completedCoursesExpanded)}
                                            sx={{
                                                p: 2,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Completed Courses
                                                </Typography>
                                                <Chip
                                                    label={completedCourses.length}
                                                    size="small"
                                                    sx={{
                                                        ml: 1,
                                                        backgroundColor: '#33BD95',
                                                        color: 'white'
                                                    }}
                                                />
                                            </Box>
                                            <IconButton size="small">
                                                {completedCoursesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>

                                        <Collapse in={completedCoursesExpanded}>
                                            <Divider />
                                            <Box sx={{ p: 2 }}>
                                                {completedCourses.length === 0 ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                                                        No completed courses found for this user within the selected date range.
                                                    </Typography>
                                                ) : (
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                                            Courses completed within the selected date range {completedCourses.length > COURSES_PER_PAGE && `(${completedCourses.length} total)`}:
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ minHeight: 'auto' }}>
                                                            {currentPageCompletedCourses.map((course) => {
                                                                const scorePercentage = getCourseScorePercentage(course);
                                                                return (
                                                                    <Grid item xs={6} key={course.UniqueIdentifier}>
                                                                        <MainCard
                                                                            content={false}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                border: '1px solid',
                                                                                display: 'flex',
                                                                                flexDirection: 'column'
                                                                            }}
                                                                            border={true}
                                                                            boxShadow={false}
                                                                        >
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 500,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        display: '-webkit-box',
                                                                                        WebkitLineClamp: 2,
                                                                                        WebkitBoxOrient: 'vertical',
                                                                                        lineHeight: 1.3,
                                                                                        flex: 1
                                                                                    }}
                                                                                >
                                                                                    {course.CourseId}
                                                                                </Typography>
                                                                                {/* Score chip */}
                                                                                {scorePercentage !== null && (
                                                                                    <Chip
                                                                                        label={`${scorePercentage}%`}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            height: 20,
                                                                                            fontSize: '0.70rem',
                                                                                            fontWeight: 'bold',
                                                                                            ml: 1,
                                                                                            backgroundColor: scorePercentage >= 50 ? '#33BD95' : '#FFBD54',
                                                                                            color: 'white',
                                                                                            '& .MuiChip-label': {
                                                                                                px: 0.5
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="caption" color="textSecondary">
                                                                                Completed: {new Date(course.DateCompleted).toLocaleDateString()}
                                                                            </Typography>
                                                                        </MainCard>
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>

                                                        {totalCompletedPages > 1 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                <GlobalPagination
                                                                    count={totalCompletedPages}
                                                                    page={completedCoursesPage}
                                                                    onChange={handleCompletedCoursesPageChange}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </MainCard>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="error">Failed to load user data</Typography>
                    )}
                </Box>
            </Box>

            <Dialog
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: theme.borderRadius.sm,
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.default,
                    }
                }}
            >
                <DialogTitle>
                    Confirm Course Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete this course?
                    </Typography>
                    {courseToDelete && (
                        <Box sx={{
                            p: 2,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Course: {courseToDelete.CourseId}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Sent: {new Date(courseToDelete.DateSent).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                User: {userData?.FirstName} {userData?.LastName}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleteLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete Course'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </MainCard>
    );
};

export default UserSearch;